import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import AdminLayout from '../AdminLayout';
import { API } from '../../../lib/api';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, RefreshCw, FileText, Eye, Copy,
  MessageSquare, Bell, Mail, Smartphone, Check, X
} from 'lucide-react';

// Channel icons
const channelIcons = {
  whatsapp: MessageSquare,
  push: Bell,
  email: Mail,
  sms: Smartphone,
};

// Template Variables Reference
const TEMPLATE_VARIABLES = [
  { key: 'guest_name', desc: 'Nome completo ospite' },
  { key: 'guest_first_name', desc: 'Solo nome' },
  { key: 'booking_code', desc: 'Codice prenotazione' },
  { key: 'arrival_date', desc: 'Data arrivo' },
  { key: 'departure_date', desc: 'Data partenza' },
  { key: 'nights', desc: 'Numero notti' },
  { key: 'room_name', desc: 'Nome camera' },
  { key: 'entry_code', desc: 'Codice accesso' },
  { key: 'wifi_password', desc: 'Password WiFi' },
  { key: 'total_amount', desc: 'Totale prenotazione' },
  { key: 'paid_amount', desc: 'Importo pagato' },
  { key: 'due_amount', desc: 'Importo dovuto' },
  { key: 'payment_link', desc: 'Link pagamento' },
  { key: 'checkin_link', desc: 'Link check-in' },
  { key: 'review_link', desc: 'Link recensione' },
  { key: 'property_name', desc: 'Nome struttura' },
  { key: 'property_phone', desc: 'Telefono struttura' },
  { key: 'checkin_time', desc: 'Orario check-in' },
  { key: 'checkout_time', desc: 'Orario check-out' },
];

// Template Card
const TemplateCard = ({ template, onEdit, onDelete, onPreview }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">{template.name}</h3>
            <p className="text-sm text-gray-500 capitalize mt-0.5">
              {template.category?.replace(/_/g, ' ')} • {template.event?.replace(/_/g, ' ')}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${template.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {template.enabled ? 'Attivo' : 'Disattivo'}
            </span>
          </div>
        </div>
        
        {/* Channels */}
        <div className="flex gap-2 mt-3">
          {template.channels?.map(ch => {
            const Icon = channelIcons[ch] || FileText;
            return (
              <span key={ch} className="px-2 py-1 bg-gray-100 rounded-lg text-xs flex items-center gap-1 text-gray-700">
                <Icon className="w-3 h-3" />
                {ch}
              </span>
            );
          })}
        </div>

        {/* Body Preview */}
        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-700 line-clamp-3 whitespace-pre-wrap">
            {template.body}
          </p>
        </div>
      </div>
      
      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
        <button
          onClick={() => onPreview(template)}
          className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
        >
          <Eye className="w-4 h-4" />
          Anteprima
        </button>
        <div className="flex gap-1">
          <button
            onClick={() => onEdit(template)}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            title="Modifica"
          >
            <Edit className="w-4 h-4 text-gray-600" />
          </button>
          <button
            onClick={() => onDelete(template.id)}
            className="p-2 hover:bg-red-100 rounded-lg transition-colors"
            title="Elimina"
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Template Form Modal
const TemplateModal = ({ isOpen, onClose, onSave, template, categories, events }) => {
  const [form, setForm] = useState({
    name: '',
    category: 'booking_confirmation',
    event: 'booking_created',
    subject: '',
    body: '',
    channels: ['whatsapp'],
    enabled: true,
    language: 'it'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (template) {
      setForm({
        name: template.name || '',
        category: template.category || 'booking_confirmation',
        event: template.event || 'booking_created',
        subject: template.subject || '',
        body: template.body || '',
        channels: template.channels || ['whatsapp'],
        enabled: template.enabled ?? true,
        language: template.language || 'it'
      });
    } else {
      setForm({
        name: '',
        category: 'booking_confirmation',
        event: 'booking_created',
        subject: '',
        body: '',
        channels: ['whatsapp'],
        enabled: true,
        language: 'it'
      });
    }
  }, [template]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form, template?.id);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const toggleChannel = (ch) => {
    setForm(prev => ({
      ...prev,
      channels: prev.channels.includes(ch)
        ? prev.channels.filter(c => c !== ch)
        : [...prev.channels, ch]
    }));
  };

  const insertVariable = (varKey) => {
    const textarea = document.getElementById('template-body');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newBody = form.body.substring(0, start) + `{{${varKey}}}` + form.body.substring(end);
    setForm({...form, body: newBody});
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + varKey.length + 4, start + varKey.length + 4);
    }, 0);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="font-bold text-gray-900 text-lg">
            {template ? 'Modifica Template' : 'Nuovo Template'}
          </h3>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Nome Template</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#C5A059]"
                  placeholder="Es: Conferma Prenotazione"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Categoria</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({...form, category: e.target.value})}
                    className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900"
                  >
                    {categories.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Evento Trigger</label>
                  <select
                    value={form.event}
                    onChange={(e) => setForm({...form, event: e.target.value})}
                    className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900"
                  >
                    {events.map(e => (
                      <option key={e.value} value={e.value}>{e.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Canali</label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(channelIcons).map(([ch, Icon]) => (
                    <button
                      key={ch}
                      type="button"
                      onClick={() => toggleChannel(ch)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                        form.channels.includes(ch)
                          ? 'bg-green-100 text-green-700 border border-green-300'
                          : 'bg-gray-100 text-gray-500 border border-gray-200'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {ch}
                      {form.channels.includes(ch) && <Check className="w-4 h-4" />}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Oggetto (per Email/Push)</label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) => setForm({...form, subject: e.target.value})}
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900"
                  placeholder="Es: Conferma prenotazione {{booking_code}}"
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.enabled}
                    onChange={(e) => setForm({...form, enabled: e.target.checked})}
                    className="w-4 h-4 rounded border-gray-300 text-green-500"
                  />
                  <span className="text-sm text-gray-700">Attivo</span>
                </label>
                <select
                  value={form.language}
                  onChange={(e) => setForm({...form, language: e.target.value})}
                  className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm"
                >
                  <option value="it">Italiano</option>
                  <option value="en">English</option>
                </select>
              </div>
            </div>

            {/* Right Column - Body & Variables */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Corpo del messaggio</label>
                <textarea
                  id="template-body"
                  value={form.body}
                  onChange={(e) => setForm({...form, body: e.target.value})}
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 resize-none font-mono text-sm"
                  rows={12}
                  placeholder="Ciao {{guest_name}},&#10;&#10;Ti confermiamo la prenotazione..."
                  required
                />
              </div>

              {/* Variables Helper */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-medium text-gray-700 mb-2">Variabili disponibili (clicca per inserire)</p>
                <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                  {TEMPLATE_VARIABLES.map(v => (
                    <button
                      key={v.key}
                      type="button"
                      onClick={() => insertVariable(v.key)}
                      className="px-2 py-1 bg-white border border-gray-200 rounded text-xs hover:bg-gray-100 transition-colors"
                      title={v.desc}
                    >
                      {`{{${v.key}}}`}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-6 border-t border-gray-200 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium transition-colors"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-3 bg-[#C5A059] hover:bg-[#B8934D] rounded-lg text-white font-medium disabled:opacity-50 transition-colors shadow-sm"
            >
              {saving ? 'Salvataggio...' : 'Salva Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Preview Modal
const PreviewModal = ({ isOpen, onClose, template }) => {
  const { token } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vars, setVars] = useState({
    guest_name: 'Mario Rossi',
    booking_code: 'MDP-12345',
    arrival_date: '20/07/2026',
    departure_date: '27/07/2026',
    property_name: 'La Maisonette di Paestum',
    checkin_time: '15:00',
    entry_code: '1234',
  });

  useEffect(() => {
    if (isOpen && template) {
      fetchPreview();
    }
  }, [isOpen, template, vars]);

  const fetchPreview = async () => {
    if (!template) return;
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/communication/templates/${template.id}/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ template_id: template.id, variables: vars })
      });
      if (response.ok) {
        const data = await response.json();
        setPreview(data);
      }
    } catch (error) {
      console.error('Preview error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !template) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-bold text-gray-900 text-lg">Anteprima: {template.name}</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        <div className="p-6">
          {/* Quick variables */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs font-medium text-gray-700 mb-2">Modifica variabili per test</p>
            <div className="grid grid-cols-2 gap-2">
              <input
                value={vars.guest_name}
                onChange={(e) => setVars({...vars, guest_name: e.target.value})}
                className="px-2 py-1 border rounded text-sm"
                placeholder="Nome ospite"
              />
              <input
                value={vars.booking_code}
                onChange={(e) => setVars({...vars, booking_code: e.target.value})}
                className="px-2 py-1 border rounded text-sm"
                placeholder="Codice prenotazione"
              />
            </div>
          </div>

          {/* Preview */}
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 mx-auto animate-spin text-gray-400" />
            </div>
          ) : preview ? (
            <div className="space-y-4">
              {preview.subject && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">OGGETTO</p>
                  <p className="text-gray-900 font-medium">{preview.subject}</p>
                </div>
              )}
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">MESSAGGIO</p>
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <p className="text-gray-900 whitespace-pre-wrap">{preview.body}</p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-center">Impossibile caricare anteprima</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default function TemplatesPage() {
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [filter, setFilter] = useState('');

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API}/admin/communication/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Filter only new format templates (with 'name' field)
        setTemplates((data.templates || []).filter(t => t.name));
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Errore nel caricamento template');
    } finally {
      setLoading(false);
    }
  };

  const fetchMetadata = async () => {
    try {
      const [catRes, evtRes] = await Promise.all([
        fetch(`${API}/admin/communication/template-categories`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/communication/automation-events`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (catRes.ok) {
        const data = await catRes.json();
        setCategories(data.categories || []);
      }
      if (evtRes.ok) {
        const data = await evtRes.json();
        setEvents(data.events || []);
      }
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  };

  useEffect(() => {
    if (token) {
      fetchTemplates();
      fetchMetadata();
    }
  }, [token]);

  const handleSave = async (form, templateId) => {
    try {
      const url = templateId 
        ? `${API}/admin/communication/templates/${templateId}`
        : `${API}/admin/communication/templates`;
      
      const response = await fetch(url, {
        method: templateId ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });
      
      if (response.ok) {
        toast.success(templateId ? 'Template aggiornato' : 'Template creato');
        fetchTemplates();
      } else {
        const error = await response.json();
        // Handle Pydantic validation errors (array format)
        if (Array.isArray(error.detail)) {
          const messages = error.detail.map(e => e.msg || e.message || 'Errore').join(', ');
          toast.error(`Validazione fallita: ${messages}`);
        } else if (typeof error.detail === 'string') {
          toast.error(error.detail);
        } else {
          toast.error('Errore nel salvataggio');
        }
      }
    } catch (error) {
      toast.error('Errore di rete');
    }
  };

  const handleDelete = async (templateId) => {
    if (!confirm('Eliminare questo template?')) return;
    
    try {
      const response = await fetch(`${API}/admin/communication/templates/${templateId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Template eliminato');
        fetchTemplates();
      } else {
        toast.error('Errore nell\'eliminazione');
      }
    } catch (error) {
      toast.error('Errore di rete');
    }
  };

  const filteredTemplates = templates.filter(t => 
    !filter || 
    t.name?.toLowerCase().includes(filter.toLowerCase()) ||
    t.category?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <AdminLayout title="Message Templates">
      <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Template Messaggi</h1>
            <p className="text-gray-500 mt-1">Gestisci i template per le comunicazioni automatiche</p>
          </div>
          <button
            onClick={() => { setEditingTemplate(null); setShowModal(true); }}
            className="px-4 py-2.5 bg-[#C5A059] hover:bg-[#B8934D] text-white rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Nuovo Template
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Cerca template..."
            className="w-full md:w-80 px-4 py-2.5 bg-white border border-gray-200 rounded-lg"
          />
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-64 bg-gray-200 rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun template trovato</h3>
            <p className="text-gray-500 mb-4">Crea il tuo primo template per le comunicazioni automatiche</p>
            <button
              onClick={() => { setEditingTemplate(null); setShowModal(true); }}
              className="px-4 py-2 bg-[#C5A059] hover:bg-[#B8934D] text-white rounded-lg font-medium"
            >
              <Plus className="w-4 h-4 inline mr-1" />
              Crea Template
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map(template => (
              <TemplateCard
                key={template.id}
                template={template}
                onEdit={(t) => { setEditingTemplate(t); setShowModal(true); }}
                onDelete={handleDelete}
                onPreview={(t) => setPreviewTemplate(t)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <TemplateModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingTemplate(null); }}
        onSave={handleSave}
        template={editingTemplate}
        categories={categories}
        events={events}
      />

      <PreviewModal
        isOpen={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        template={previewTemplate}
      />
    </AdminLayout>
  );
}
